import axios, { AxiosError } from 'axios';
import { clearAuthTokens } from '../../features/auth/guard/utils';
import router from '@/core/global-router';
import { toast } from 'sonner';
import { API_GATEWAY_BASE_URL } from '@/core/global-data/constants.data';

// ------------- AXIOS DEFAULTS SETTINGS -------------
// ---------------------------------------------------
const defaultURL = API_GATEWAY_BASE_URL;

axios.defaults.baseURL = defaultURL;

// ------------- GLOBAL ERROR HANDLER -------------

const handleUnauthorized = () => {
    // 1. Clear authentication tokens
    clearAuthTokens();

    // 2. Redirect to login page
    router.navigate('/login');

    // 3. Optional: Refresh the page to clear application state
    window.location.reload();
};

// ------------- CREATE AXIOS INTERCEPTORS -------------
// ----------------------------------------------------

axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const status = error.response?.status;
        const isUnauthorized = status === 401;

        if (isUnauthorized && !error.config?.url?.includes('/login')) {
            handleUnauthorized();
        }

        if (status === 403 && error.response?.data?.message) {
            toast.error(error.response?.data?.message);
        }

        return Promise.reject(error);
    },
);

// -------------------------------------------------------

export const endpoints = {
    auth: {
        login: '/login',
        profile: '/user',
        me: '/me',
        changePassword: '/change-password',
        forgotPassword: '/forgot-password',
        resetPassword: '/reset-password',
    },
    dashboard: {
        analytics: '/dashboard',
    },

    customer: {
        create: '/customers-store',
        update: '/customers-update/',
        delete: '/customers-delete/',
        list: '/customers-paginated',
        select: '/customers-simple',
        show: '/customers-show/',
        block: '/customers-add-to-black-list/',
        logs: '/logs/user',
        logscleanup: '/logs/cleanup/',

        dashboard: '/customers-analytics',
        dashboardType: '/customers-analytics-type',
        images: '/customer-update-personal-images/',
        license: '/customer-update-license-images/',
        dashboardNewCustomers: '/customer-new-dashboard',
        typeTrack: '/customers-type-tracks/',
        typeTracks: '/customers-type-tracks',
    },

    complaint: {
        list: '/customer-complaints-paginated',
        show: '/customer-complaints-show/',
        update: '/customer-complaints-resolve/',
        // update: '/customers-complaints/',
        create: '/customer-complaints-store',
        delete: '/customers-delete/',
        resolve: '/customer-complaints-resolve/',
        dashboard: '/customer-complaints-statistics',
        open: '/customer-complaints-open'
    },

    cutomerContact: {
        list: '/customer-emails-paginated',
        create: '/customer-emails-store',
        dashboard: '/customer-emails-statistics',
        show: '/customer-emails-show/', //customer-emails-show
        update: '/customer-emails-update',
        email: '/customers-emails/',
    },

    services: {
        create: '/services-store',
        update: '/services-update',
        delete: '/services-delete/',
        list: '/services-paginated',
        show: '/services-show',
        dashboard: '/services-analytics',
        select: '/services-simple',
    },

    products: {
        list: '/products-paginated',
        create: '/products-store',
        update: '/products-update',
        delete: '/products-delete/',
        show: '/products-show',
        dashboard: '/products-analytics',
        select: '/products-simple',
    },
    finance: {
        payments: '/orders-payment-analytics',
        
    },
    notification: {
        list: '/notification-list',
        count: '/notification-count',
        delete: '/notification-delete/',
        read: '/notification-mark-read/',
        allRead: '/notification-mark-all-read',
        mail: '/notification-mail',
        generate: '/notification-generate',
        sendEmail: '/notification-send-emails',
    },

    orderproducts: {
        list: '/product-orders-paginated',
        create: '/product-orders-store',
        update: '/product-orders-update',
        delete: '/product-orders-delete/',
        show: '/product-orders-show',
        dashboard: '/product-orders-analytics',
        post: '/website-product-service',
        get: '/website-products',
        // select: '/products-simple',
    },
    branch: {
        create: '/branches-store',
        list: '/branches-paginated',
        show: '/branches-show/',
        update: '/branches-update/',
        delete: '/branches-delete/',
        dashboard: 'branches-analytics',
        select: 'branches-simple',
    },
    employee: {
        list: '/users-paginated',
        show: '/users-show/',
        create: '/users-store',
        update: '/users-update/',
        delete: '/users-delete/',
        dashboard: '/users-analytics',
        select: '/users-simple',
        attendance: '/users-attendance',
        salary: '/users-salary/',
        salaryList: '/users-salary-list',
        logs: '/user-logs/user/',
        usersOrdersProducts: '/users-orders-and-product-orders/',
    },
    roles: {
        list: '/role-list',
        create: '/role-create',
        update: '/role-update/',
        delete: '/role-delete/',
        show: '/role-permissions/',
    },
    permissions: {
        list: '/permission-list',
        create: '/permission-create',
        update: '/permission-update/',
        delete: '/permission-delete/',
    },
    userLimits: {
        list: '/user-limits',
    },
    orders: {
        create: '/orders-store',
        update: '/orders-update',
        delete: '/orders-delete/',
        list: '/orders-paginated',
        show: '/orders-show',
        dashboard: '/orders-analytics',
        status: '/orders-update-status'
    },
    subscriptions: {
        list: '/subscriptions-paginated',
        create: '/subscriptions-store',
        dashboard: '/subscriptions-analytics',
        update: '/subscriptions-update/',
        delete: '/subscriptions-delete/',
        show: '/subscriptions-show/',
        subscriptions: '/customer-subscriptions',
    },

    

    loan: {
        list: '/loan-request-list-paginate',
        show: '/loan-request-show/',
        update: '/loan-request-update/',
        create: '/loan-request-create',
        delete: '/loan-request-delete/',
    },
    letterRequests: {
        paginate: '/letter-request-list-paginate',
        create: '/letter-request-create',
        update: '/letter-request-update/',
        delete: '/letter-request-delete/',
    },
    extraServices: {
        list: '/service-request-list-paginate',
        show: '/service-request-show/',
        create: '/service-request-create',
        update: '/service-request-update/',
        delete: '/service-request-delete/',
    },
    employeeComplaint: {
        list: '/employee-complaint-list-paginate',
        show: '/employee-complaint-show/',
        update: '/employee-complaint-update/',
        create: '/employee-complaint-create',
        delete: '/employee-complaint-delete/',
    },
};
